import type { FC, FormEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { BurstPiece } from '@/components/contact/burst'
import type { SupportPlan, SupportTopic } from '@/constants/help'

import { useEffect, useId, useState } from 'react'
import { useRouter } from 'next/router'

import {
    ArrowRight,
    Check,
    CreditCard,
    Handshake,
    LifeBuoy,
    LoaderCircle,
    RotateCcw
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Burst, makeBurst } from '@/components/contact/burst'
import { SUPPORT_PLANS, SUPPORT_TOPICS } from '@/constants/help'

type Errors = Partial<Record<'topic' | 'name' | 'email' | 'message' | 'form', string>>

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const TOPIC_ICONS: Record<SupportTopic, LucideIcon> = {
    soporte: LifeBuoy,
    ventas: Handshake,
    facturacion: CreditCard
}

const PLAN_ALIASES = new Map<string, SupportPlan>([
    ['gratis', 'gratis'],
    ['free', 'gratis'],
    ['pro', 'pro'],
    ['empresarial', 'empresarial'],
    ['enterprise', 'empresarial']
])

const ERROR_TEXT = 'text-[#f2998c]'

const field = 'peer block w-full rounded-2xl border bg-cream/60 px-5 text-base text-ink outline-none transition-[border-color,box-shadow,background-color] duration-200 focus:border-ember focus:bg-cream focus:ring-4 focus:ring-ember/15'

const floatingLabel = 'pointer-events-none absolute left-5 text-stone transition-all duration-200 peer-focus:top-3 peer-focus:translate-y-0 peer-focus:text-xs peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs'

const legendClass = 'font-mono text-[11px] uppercase tracking-[0.16em] text-stone'

/** Lowercases and strips accents so `?tema=Facturación` still matches. */
const readQuery = (value: unknown): string => (typeof value === 'string'
    ? value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()
    : '')

interface FloatingFieldProps {
    id: string
    label: string
    error?: string
    children: ReactNode
    multiline?: boolean
}

const FloatingField: FC<FloatingFieldProps> = ({ id, label, error, children, multiline = false }) => {
    return (
        <div>
            <div className='relative'>
                {children}
                <label
                    htmlFor={id}
                    className={cn(floatingLabel, multiline ? 'top-5 text-base' : 'top-1/2 -translate-y-1/2 text-base')}
                >
                    {label}
                </label>
            </div>
            {error && (
                <p
                    id={`${id}-error`}
                    role='alert'
                    className={cn('mt-2 pl-2 text-sm', ERROR_TEXT)}
                >
                    {error}
                </p>
            )}
        </div>
    )
}

export const SupportForm: FC = () => {
    const router = useRouter()
    const id = useId()
    const [topic, setTopic] = useState<SupportTopic>('soporte')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [company, setCompany] = useState('')
    const [phone, setPhone] = useState('')
    const [plan, setPlan] = useState<SupportPlan | ''>('')
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<Errors>({})
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
    const [pieces, setPieces] = useState<Array<BurstPiece>>([])

    useEffect(() => {
        if (!router.isReady) return

        const tema = readQuery(router.query.tema)
        const planFromQuery = PLAN_ALIASES.get(readQuery(router.query.plan))
        const topicFromQuery = SUPPORT_TOPICS.find(item => item.value === tema)

        if (topicFromQuery) setTopic(topicFromQuery.value)
        else if (planFromQuery) setTopic('ventas')

        if (planFromQuery) setPlan(planFromQuery)
    }, [router.isReady, router.query.tema, router.query.plan])

    const activeTopic = SUPPORT_TOPICS.find(item => item.value === topic) ?? SUPPORT_TOPICS[0]

    const validate = (): Errors => {
        const next: Errors = {}

        if (name.trim().length < 2) next.name = 'Escribe tu nombre para saber cómo llamarte.'
        if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Revisa tu correo, parece que tiene un error.'
        if (message.trim().length < 10) next.message = 'Cuéntanos un poco más, al menos 10 caracteres.'

        return next
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate()

        setErrors(found)

        if (Object.keys(found).length) {
            const first = Object.keys(found)[0]

            document.getElementById(`${id}-${first}`)?.focus()

            return
        }

        setStatus('sending')

        try {
            const response = await fetch('/api/support', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic,
                    name: name.trim(),
                    email: email.trim(),
                    company: company.trim(),
                    phone: phone.trim(),
                    plan,
                    message: message.trim(),
                    website
                })
            })

            const data = await response.json().catch(() => ({})) as { message?: string, errors?: Errors }

            if (!response.ok) {
                setErrors({ ...(data.errors ?? {}), form: data.message ?? 'Algo salió mal. Inténtalo de nuevo.' })
                setStatus('idle')

                return
            }

            setStatus('sent')

            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) setPieces(makeBurst())
        }
        catch {
            setErrors({ form: 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.' })
            setStatus('idle')
        }
    }

    const startOver = () => {
        setName('')
        setEmail('')
        setCompany('')
        setPhone('')
        setPlan('')
        setMessage('')
        setErrors({})
        setPieces([])
        setStatus('idle')
    }

    if (status === 'sent') {
        return (
            <div
                role='status'
                className='relative flex min-h-[600px] flex-col items-center justify-center overflow-visible rounded-[2rem] border border-ink/10 bg-paper p-8 text-center md:p-12'
            >
                <Burst pieces={pieces} />

                <span className='relative flex size-20 items-center justify-center rounded-full bg-ember text-cream'>
                    <Check
                        className='size-9'
                        strokeWidth={2.5}
                    />
                </span>
                <span className='stamp mt-8 -rotate-3 text-ember'>Ticket registrado</span>
                <h2 className='mt-6 text-[clamp(2.25rem,4vw,3.25rem)] font-bold leading-[0.95] tracking-[-0.035em]'>
                    Recibimos tu mensaje.
                </h2>
                <p className='mt-4 max-w-sm text-lg leading-relaxed text-stone'>
                    Gracias, {name.trim().split(' ')[0]}. Te respondemos por correo a <span className='font-semibold text-ink'>{email.trim()}</span>.
                </p>
                <button
                    type='button'
                    onClick={startOver}
                    className='mt-8 inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-3 text-sm font-medium transition-colors hover:border-ink hover:bg-ink hover:text-cream'
                >
                    <RotateCcw className='size-4' />
                    Enviar otro mensaje
                </button>
            </div>
        )
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            aria-labelledby={`${id}-title`}
            className='relative rounded-[2rem] border border-ink/10 bg-paper p-6 shadow-[0_40px_80px_-60px_rgb(0_0_0/0.5)] md:p-10'
        >
            <span
                aria-hidden='true'
                className='tape -top-3 left-10 -rotate-2'
            />

            <div className='flex flex-wrap items-start justify-between gap-4'>
                <div>
                    <p className={legendClass}>Formulario de soporte</p>
                    <h2
                        id={`${id}-title`}
                        className='mt-3 text-[clamp(2rem,3.4vw,2.75rem)] font-bold leading-[0.95] tracking-[-0.04em]'
                    >
                        Abrir un ticket
                    </h2>
                    <p className='mt-3 max-w-md text-base leading-relaxed text-stone'>
                        Elige el tema, cuéntanos qué necesitas y te respondemos por correo.
                    </p>
                </div>
                <span className='stamp rotate-2 text-ink/70'>Ticket nuevo</span>
            </div>

            <div className='rule-hair mt-8' />

            <fieldset className='mt-8'>
                <legend className={legendClass}>Tema de tu solicitud</legend>
                <div className='mt-3 grid gap-1.5 rounded-[1.25rem] bg-sand p-1.5 sm:grid-cols-3'>
                    {SUPPORT_TOPICS.map((item, index) => {
                        const Icon = TOPIC_ICONS[item.value]
                        const active = topic === item.value

                        return (
                            <label
                                key={item.value}
                                className='cursor-pointer'
                            >
                                <input
                                    id={index === 0 ? `${id}-topic` : undefined}
                                    type='radio'
                                    name='topic'
                                    value={item.value}
                                    checked={active}
                                    onChange={() => setTopic(item.value)}
                                    aria-describedby={errors.topic ? `${id}-topic-error` : undefined}
                                    className='peer sr-only'
                                />
                                <span
                                    className={cn(
                                        'flex h-full items-start gap-3 rounded-[0.9rem] px-4 py-3.5 transition-[background-color,color,box-shadow] duration-200 peer-focus-visible:ring-4 peer-focus-visible:ring-ember/30',
                                        active ? 'bg-ink text-cream shadow-[0_12px_24px_-14px_rgb(0_0_0/0.7)]' : 'text-ink hover:bg-paper/70'
                                    )}
                                >
                                    <Icon className={cn('mt-0.5 size-4 shrink-0', active ? 'text-cream' : 'text-ink/70')} />
                                    <span>
                                        <span className='block text-sm font-semibold'>{item.label}</span>
                                        <span className={cn('mt-0.5 block text-xs leading-snug', active ? 'text-cream/70' : 'text-stone')}>{item.hint}</span>
                                    </span>
                                </span>
                            </label>
                        )
                    })}
                </div>
                {errors.topic && (
                    <p
                        id={`${id}-topic-error`}
                        role='alert'
                        className={cn('mt-2 pl-2 text-sm', ERROR_TEXT)}
                    >
                        {errors.topic}
                    </p>
                )}
            </fieldset>

            <div className='mt-8 grid gap-4 sm:grid-cols-2'>
                <FloatingField
                    id={`${id}-name`}
                    label='Tu nombre'
                    error={errors.name}
                >
                    <input
                        id={`${id}-name`}
                        name='name'
                        autoComplete='name'
                        maxLength={80}
                        placeholder=' '
                        value={name}
                        onChange={event => setName(event.target.value)}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? `${id}-name-error` : undefined}
                        className={cn(field, 'h-16 pt-5', errors.name ? 'border-[#f2998c]' : 'border-ink/12')}
                    />
                </FloatingField>

                <FloatingField
                    id={`${id}-email`}
                    label='Correo electrónico'
                    error={errors.email}
                >
                    <input
                        id={`${id}-email`}
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        maxLength={254}
                        placeholder=' '
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? `${id}-email-error` : undefined}
                        className={cn(field, 'h-16 pt-5', errors.email ? 'border-[#f2998c]' : 'border-ink/12')}
                    />
                </FloatingField>

                <FloatingField
                    id={`${id}-company`}
                    label='Empresa o RNC (opcional)'
                >
                    <input
                        id={`${id}-company`}
                        name='company'
                        autoComplete='organization'
                        maxLength={160}
                        placeholder=' '
                        value={company}
                        onChange={event => setCompany(event.target.value)}
                        className={cn(field, 'h-16 border-ink/12 pt-5')}
                    />
                </FloatingField>

                <FloatingField
                    id={`${id}-phone`}
                    label='Teléfono o WhatsApp (opcional)'
                >
                    <input
                        id={`${id}-phone`}
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        inputMode='tel'
                        maxLength={40}
                        placeholder=' '
                        value={phone}
                        onChange={event => setPhone(event.target.value)}
                        className={cn(field, 'h-16 border-ink/12 pt-5')}
                    />
                </FloatingField>
            </div>

            <div className='mt-8'>
                <p
                    id={`${id}-plan-label`}
                    className={legendClass}
                >
                    ¿Qué plan te interesa? <span className='normal-case tracking-normal'>(opcional)</span>
                </p>
                <div
                    role='group'
                    aria-labelledby={`${id}-plan-label`}
                    className='mt-3 flex flex-wrap gap-2'
                >
                    {SUPPORT_PLANS.map(option => {
                        const active = plan === option.value

                        return (
                            <button
                                key={option.value}
                                type='button'
                                aria-pressed={active}
                                onClick={() => setPlan(active ? '' : option.value)}
                                className={cn(
                                    'inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium outline-none transition-[background-color,border-color,color] duration-200 focus-visible:ring-4 focus-visible:ring-ember/30',
                                    active ? 'border-ember bg-ember text-cream' : 'border-ink/12 hover:border-ink/40'
                                )}
                            >
                                {active && <Check className='size-4' />}
                                Plan {option.label}
                            </button>
                        )
                    })}
                </div>
            </div>

            <div className='mt-8'>
                <FloatingField
                    id={`${id}-message`}
                    label={activeTopic.messageLabel}
                    error={errors.message}
                    multiline
                >
                    <textarea
                        id={`${id}-message`}
                        name='message'
                        rows={5}
                        maxLength={4000}
                        placeholder=' '
                        value={message}
                        onChange={event => setMessage(event.target.value)}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? `${id}-message-error` : undefined}
                        className={cn(field, 'resize-y pb-4 pt-8 leading-relaxed', errors.message ? 'border-[#f2998c]' : 'border-ink/12')}
                    />
                </FloatingField>
            </div>

            <div
                aria-hidden='true'
                className='absolute -left-[9999px] h-px w-px overflow-hidden'
            >
                <label htmlFor={`${id}-website`}>Sitio web</label>
                <input
                    id={`${id}-website`}
                    name='website'
                    tabIndex={-1}
                    autoComplete='off'
                    value={website}
                    onChange={event => setWebsite(event.target.value)}
                />
            </div>

            {errors.form && (
                <p
                    role='alert'
                    className='mt-6 rounded-2xl bg-[#f2998c]/10 px-4 py-3 text-sm text-[#f7b5ab]'
                >
                    {errors.form}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-sm text-stone'>
                    Cada solicitud queda registrada como un ticket y te respondemos por correo.
                </p>
                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='group inline-flex h-14 items-center justify-center gap-4 rounded-full bg-ember pl-7 pr-2 text-base font-medium text-cream shadow-[0_14px_34px_-16px_rgb(0_0_0/0.8)] transition-colors hover:bg-ember/85 disabled:opacity-70'
                >
                    {status === 'sending' ? 'Enviando' : 'Enviar solicitud'}
                    <span className='flex size-10 items-center justify-center rounded-full bg-cream text-ink'>
                        {status === 'sending'
                            ? <LoaderCircle className='size-4 animate-spin' />
                            : <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />}
                    </span>
                </button>
            </div>
        </form>
    )
}

export default SupportForm
